import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
// Generated Routes
import { useAuthStore } from '@/stores/auth.store';
import { handleServerError } from '@/utils/handle-server-error';
import * as Sentry from '@sentry/react';
import axiosRetry from 'axios-retry';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { FontProvider } from './context/font-context';
import { ThemeProvider } from './context/theme-context';
import './index.css';
import { firebaseApp } from './lib/firebase';
import { routeTree } from './routeTree.gen';

Sentry.init({
  dsn: 'https://ff02cefe976de35bf412dbfa7899938e@o4508968498036736.ingest.us.sentry.io/4508968500396032',
})

const enableMocking = async () => {
  const isMswEnabled =
    String(import.meta.env.VITE_ENABLE_MSW).toLowerCase() === 'true'
  if (!import.meta.env.DEV || !isMswEnabled) return

  const { worker } = await import('./mocks/browser')
  await worker.start({
    onUnhandledRequest: 'warn',
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.error({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return (
          error instanceof AxiosError &&
          axiosRetry.isNetworkOrIdempotentRequestError(error)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('conteúdo não foi modificado!')
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast.error('Sessão expirada!', {
            description: '',
          })
          useAuthStore.getState().logout()
          const redirect = `${router.history.location.href}`
          router.navigate({ to: '/sign-in', search: { redirect } })
        }
        if (error.response?.status === 500) {
          toast.error('Internal Server Error!')
          router.navigate({ to: '/500' })
        }
        if (error.response?.status === 403) {
          // router.navigate("/forbidden", { replace: true });
        }
      }
    },
  }),
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    firebaseApp,
    store: {
      auth: useAuthStore.getState(),
    },
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  enableMocking().then(() => {
    const root = ReactDOM.createRoot(rootElement)
    root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme='system' storageKey='vite-ui-theme'>
            <TooltipProvider delayDuration={200}>
              <Toaster duration={5000} position='top-center' expand richColors />
              <FontProvider>
                <RouterProvider router={router} />
              </FontProvider>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </StrictMode>
    )
  })
}
